import { inject } from "../../di";
import type { IWritableNotificationsState, INotificationsService } from "./types";
import type { IRpcService } from "../rpc/types";
import type { IAuthState } from "../auth/types";
import type { ISseService } from "../sse/types";
import type { NotificationReceivedEvent } from "../sse/sse-events";
import type { GetNotificationsResult } from "api-contracts/rpc/notifications";

export class NotificationsService implements INotificationsService {
  @inject<IWritableNotificationsState>("writableNotificationsState")
  private state!: IWritableNotificationsState;

  @inject<IRpcService>("rpcService")
  private rpcService!: IRpcService;

  @inject<IAuthState>("authState")
  private authState!: IAuthState;

  @inject<ISseService>("sseService")
  private sseService!: ISseService;

  private pendingLoad: Promise<void> | undefined;
  private sseUnsubscribers: Array<() => void> = [];

  initialize(): void {
    if (this.sseUnsubscribers.length > 0) return;

    // A new in-app notification was created for someone — refresh if it's us.
    this.sseUnsubscribers.push(
      this.sseService.on<NotificationReceivedEvent>("notification-received", (event) => {
        if (event.userId === this.authState.currentUser.get()?.id) {
          this.loadNotifications();
        }
      }),
    );

    // Chat unreads feed the same badge total; refresh when a chat surface changes.
    for (const eventType of ["live-chat-message", "community-chat-message", "chat-message-deleted"]) {
      this.sseUnsubscribers.push(this.sseService.on(eventType, () => this.loadNotifications()));
    }
  }

  dispose(): void {
    for (const unsubscribe of this.sseUnsubscribers) unsubscribe();
    this.sseUnsubscribers = [];
  }

  loadNotifications(): void {
    if (this.pendingLoad) return;

    this.state.loading.set(true);
    this.pendingLoad = this.doLoad();
  }

  private async doLoad(): Promise<void> {
    try {
      await this.authState.ready;
      if (!this.authState.isAuthenticated.get()) {
        return;
      }

      const result = await this.rpcService.call<GetNotificationsResult>("get-notifications", {
        limit: "50",
      });

      this.state.notifications.set(result.notifications);
      this.state.unreadCount.set(parseInt(result.unreadCount));
      this.state.chatNotifications.set(result.chatNotifications);
    } catch (error) {
      console.error("[NotificationsService] Error loading notifications:", error);
    } finally {
      this.state.loading.set(false);
      this.pendingLoad = undefined;
    }
  }

  async markAllRead(): Promise<void> {
    const currentNotifications = this.state.notifications.get();
    const hasUnread = currentNotifications.some((n) => !n.isRead);
    const hasChatUnread = this.state.chatNotifications.get().length > 0;
    if (!hasUnread && !hasChatUnread) return;

    try {
      await this.rpcService.call("mark-all-notifications-read", {});

      const updated = currentNotifications.map((n) => ({
        ...n,
        isRead: true,
        readAt: n.readAt ?? new Date().toISOString(),
      }));
      this.state.notifications.set(updated);
      this.state.chatNotifications.set([]);
      this.state.unreadCount.set(0);
    } catch (error) {
      console.error("[NotificationsService] Error marking notifications as read:", error);
    }
  }
}
